<template>
  <main class="program-admin-page">
    <div class="program-admin-shell">
      <MarketingToolHeader
        eyebrow="Group Fitness"
        title="HYROX Programme"
        description="Verwalte Titel, Beschreibungen, Hinweise und Dokumente übersichtlich an einem Ort."
        icon="mdi-clipboard-text-play-outline">
        <template v-slot:actions>
          <v-btn icon class="icon-action" aria-label="Zurück zu den Programmen" title="Zurück zu den Programmen" @click="$router.push('/employee-courses')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-btn outlined color="#58d388" class="secondary-action" :loading="loading || studiosLoading" @click="refreshPrograms">
            <v-icon left>mdi-refresh</v-icon>Aktualisieren
          </v-btn>
        </template>
      </MarketingToolHeader>

      <section v-if="canManage" class="program-admin-content">
        <div class="overview-row">
          <div>
            <span class="section-eyebrow">Programmverwaltung</span>
            <h2>Trainingspläne verwalten</h2>
            <p>Alle Studios im Überblick. Veröffentliche Programme gezielt für ein oder mehrere Studios.</p>
          </div>
          <v-btn color="#58d388" class="create-action black--text" depressed @click="openDialog()">
            <v-icon left>mdi-plus</v-icon>Neues Programm
          </v-btn>
        </div>

        <v-alert color="#58d388" text outlined class="publish-note">
          <v-icon slot="prepend" color="#58d388">mdi-bell-off-outline</v-icon>
          Beim Veröffentlichen werden keine Push-Nachrichten oder E-Mails versendet.
        </v-alert>

        <v-autocomplete
          v-model="studioFilter"
          :items="studioOptions"
          dark
          color="#58d388"
          :menu-props="{ dark: true }"
          item-text="name"
          item-value="_id"
          label="Nach Studio filtern"
          placeholder="Alle Studios"
          persistent-placeholder
          clearable
          outlined
          hide-details
          class="program-studio-filter" />

        <div v-if="loading" class="loading-state">
          <v-progress-circular indeterminate color="#58d388" size="48" width="4" />
          <span>Programme werden geladen ...</span>
        </div>

        <div v-else-if="filteredPrograms.length" class="program-grid">
          <article v-for="program in filteredPrograms" :key="program._id" class="program-card" :class="`status-${program.status}`">
            <header>
              <span class="program-icon"><v-icon>mdi-run-fast</v-icon></span>
              <div class="program-heading">
                <small>{{ formatDate(program.validFrom) }}</small>
                <h3>{{ program.title }}</h3>
              </div>
              <span class="status-badge" :class="program.status">{{ statusLabel(program.status) }}</span>
            </header>

            <p>{{ program.intro || "Keine Einleitung hinterlegt." }}</p>
            <div class="program-studios" aria-label="Sichtbar in diesen Studios">
              <span v-if="!program.studios || !program.studios.length">Alle Studios</span>
              <span v-for="studio in program.studios" :key="studio._id">{{ studio.name }}</span>
            </div>

            <div class="program-meta">
              <span><v-icon>mdi-view-grid-outline</v-icon>{{ program.blocks.length }} Inhalte</span>
              <span><v-icon>mdi-update</v-icon>{{ formatUpdatedAt(program.updatedAt) }}</span>
            </div>

            <footer>
              <v-btn text color="#58d388" @click="openDialog(program)"><v-icon left>mdi-pencil-outline</v-icon>Bearbeiten</v-btn>
              <v-btn icon color="#ff8d98" aria-label="Programm löschen" @click="deleteProgram(program)"><v-icon>mdi-delete-outline</v-icon></v-btn>
            </footer>
          </article>
        </div>

        <div v-else class="empty-state">
          <span><v-icon>mdi-clipboard-text-outline</v-icon></span>
          <h3>{{ studioFilter ? 'Keine Programme für dieses Studio' : 'Noch keine HYROX Programme' }}</h3>
          <p>{{ studioFilter ? 'Wähle ein anderes Studio oder zeige wieder alle Programme an.' : 'Erstelle den ersten Trainingsplan und veröffentliche ihn, sobald alle Inhalte bereit sind.' }}</p>
          <v-btn v-if="studioFilter" text color="#58d388" @click="studioFilter = null">Alle Programme anzeigen</v-btn>
          <v-btn color="#58d388" class="black--text" depressed @click="openDialog()"><v-icon left>mdi-plus</v-icon>Programm erstellen</v-btn>
        </div>
      </section>

      <section v-else class="access-state">
        <v-icon>mdi-lock-outline</v-icon>
        <h2>Keine Berechtigung</h2>
        <p>Für diese Verwaltung wird das Recht «HYROX Programme verwalten» benötigt.</p>
      </section>
    </div>

    <v-dialog v-model="dialog" max-width="58rem" persistent dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="editor-card">
        <header class="editor-header">
          <span class="editor-icon"><v-icon>{{ editingId ? "mdi-file-edit-outline" : "mdi-file-plus-outline" }}</v-icon></span>
          <div>
            <span class="section-eyebrow">HYROX Group</span>
            <h2>{{ editingId ? "Programm bearbeiten" : "Programm erstellen" }}</h2>
            <p>Ordne die Inhalte in der Reihenfolge an, in der sie später angezeigt werden.</p>
          </div>
          <v-btn icon aria-label="Editor schliessen" :disabled="saving" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </header>

        <v-card-text class="editor-body">
          <v-form ref="form" v-model="valid">
            <div class="editor-fields">
              <v-text-field
                v-model="form.title"
                label="Titel"
                placeholder="Zum Beispiel HYROX Group"
                outlined
                :rules="[
                  (value) => !!String(value || '').trim() || 'Titel erforderlich',
                  (value) => String(value || '').length <= 160 || 'Titel maximal 160 Zeichen',
                ]" />
              <v-text-field
                v-model="form.validFrom"
                type="date"
                label="Gültig ab"
                outlined
                :rules="[(value) => !!value || 'Startdatum erforderlich']" />
            </div>

            <v-autocomplete
              :value="form.studios"
              :items="studioOptions"
              item-text="name"
              item-value="_id"
              label="Sichtbar in diesen Studios"
              placeholder="Alle Studios"
              persistent-placeholder
              multiple
              chips
              deletable-chips
              clearable
              outlined
              :loading="studiosLoading"
              :disabled="saving || studiosLoading || !!studioLoadError"
              :hint="form.studios.length ? 'Du kannst mehrere Studios auswählen und die Zuordnung später ändern.' : 'Alle Studios: Ohne Auswahl ist das Programm in jedem Studio sichtbar.'"
              persistent-hint
              class="program-studio-select"
              @input="form.studios = $event || []" />
            <v-alert v-if="studioLoadError" type="warning" text class="studio-load-error">
              {{ studioLoadError }}
              <v-btn text @click="loadStudios">Erneut laden</v-btn>
            </v-alert>

            <v-textarea
              v-model="form.intro"
              label="Beschreibung (optional)"
              placeholder="Zum Beispiel: Die Zeiten können individuell an die Gruppe angepasst werden."
              outlined
              rows="3"
              counter="5000"
              :rules="[(value) => String(value || '').length <= 5000 || 'Einleitung maximal 5000 Zeichen']" />

            <v-select v-model="form.status" :items="statusItems" label="Status" outlined hide-details class="status-select" />

            <div class="content-heading">
              <div>
                <span class="section-eyebrow">Programminhalte</span>
                <h3>Hinweise und Dokumente</h3>
              </div>
              <span>{{ blocks.length }} / 50</span>
            </div>

            <div v-if="blocks.length" class="block-list">
              <article v-for="(block, index) in blocks" :key="block.clientId" class="content-block">
                <header class="block-header">
                  <span class="block-type-icon"><v-icon>{{ blockIcon(block.type) }}</v-icon></span>
                  <div>
                    <small>Inhalt {{ index + 1 }}</small>
                    <strong>{{ blockTypeLabel(block.type) }}</strong>
                  </div>
                  <div class="block-actions">
                    <v-btn icon small :disabled="index === 0" aria-label="Nach oben verschieben" @click="moveBlock(index, -1)">
                      <v-icon>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn icon small :disabled="index === blocks.length - 1" aria-label="Nach unten verschieben" @click="moveBlock(index, 1)">
                      <v-icon>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon small color="#ff8d98" aria-label="Inhalt entfernen" @click="removeBlock(index)"><v-icon>mdi-delete-outline</v-icon></v-btn>
                  </div>
                </header>

                <v-text-field
                  v-model="block.title"
                  label="Titel (optional)"
                  outlined
                  counter="160"
                  :rules="[(value) => String(value || '').length <= 160 || 'Titel maximal 160 Zeichen']" />

                <v-textarea
                  v-if="block.type === 'text' || block.type === 'notice'"
                  v-model="block.content"
                  :label="block.type === 'notice' ? 'Hinweis' : 'Text'"
                  outlined
                  rows="4"
                  counter="15000"
                  :rules="[
                    (value) => !!String(value || '').trim() || 'Inhalt erforderlich',
                    (value) => String(value || '').length <= 15000 || 'Inhalt maximal 15000 Zeichen',
                  ]" />

                <template v-else>
                  <div v-if="block.media" class="media-file-row">
                    <span class="media-file-icon" :class="`is-${block.type}`"><v-icon>{{ blockIcon(block.type) }}</v-icon></span>
                    <div class="media-file-copy">
                      <small>Gespeichertes {{ blockTypeLabel(block.type) }}</small>
                      <strong>{{ block.media.originalName }}</strong>
                      <span>{{ formatSize(block.media.size) }}</span>
                    </div>
                    <div class="media-file-actions">
                      <v-btn text color="#58d388" @click="openMediaPreview(block)">
                        <v-icon left>{{ block.type === "video" ? "mdi-play-circle-outline" : "mdi-open-in-new" }}</v-icon>
                        {{ block.type === "video" ? "Video ansehen" : "Bild ansehen" }}
                      </v-btn>
                      <v-btn outlined color="#d4d4d8" @click="replaceMedia(block)">
                        <v-icon left>mdi-swap-horizontal</v-icon>Datei ersetzen
                      </v-btn>
                    </div>
                  </div>

                  <template v-else>
                    <v-file-input
                      v-model="block.file"
                      :label="block.type === 'video' ? 'Video auswählen' : 'Bild auswählen'"
                      :accept="block.type === 'video' ? 'video/*' : 'image/*'"
                      :hint="block.type === 'video' ? 'Maximal 5 GB und 60 Minuten pro Video' : 'Bilder bis maximal 25 MB'"
                      persistent-hint
                      outlined
                      show-size
                      prepend-icon=""
                      prepend-inner-icon="mdi-paperclip"
                      :rules="[(file) => mediaFileRule(file, block.type)]"
                      @change="setPreview(block, $event)" />

                    <div v-if="block.previewUrl" class="media-file-row">
                      <span class="media-file-icon" :class="`is-${block.type}`"><v-icon>{{ blockIcon(block.type) }}</v-icon></span>
                      <div class="media-file-copy">
                        <small>Neue Datei</small>
                        <strong>{{ block.file && block.file.name }}</strong>
                        <span>{{ formatSize(block.file && block.file.size) }}</span>
                      </div>
                      <div class="media-file-actions">
                        <v-btn text color="#58d388" @click="openMediaPreview(block)">
                          <v-icon left>mdi-eye-outline</v-icon>Vorschau öffnen
                        </v-btn>
                      </div>
                    </div>
                  </template>

                  <v-textarea
                    v-model="block.content"
                    label="Beschreibung oder Coaching-Hinweis (optional)"
                    outlined
                    rows="3"
                    counter="15000"
                    :rules="[(value) => String(value || '').length <= 15000 || 'Beschreibung maximal 15000 Zeichen']" />
                </template>
              </article>
            </div>

            <div v-else class="blocks-empty">
              <v-icon>mdi-view-grid-plus-outline</v-icon>
              <span>Füge Text, Hinweise, Bilder oder Videos hinzu.</span>
            </div>

            <div class="add-block-grid">
              <button type="button" @click="addBlock('text')"><v-icon>mdi-text-box-outline</v-icon><span>Text</span></button>
              <button type="button" @click="addBlock('notice')"><v-icon>mdi-alert-circle-outline</v-icon><span>Hinweis</span></button>
              <button type="button" @click="addBlock('image')"><v-icon>mdi-image-outline</v-icon><span>Bild</span></button>
              <button type="button" @click="addBlock('video')"><v-icon>mdi-video-outline</v-icon><span>Video</span></button>
            </div>
          </v-form>

          <div v-if="saving && uploadLabel" class="upload-state">
            <div><span>{{ uploadLabel }}</span><strong>{{ uploadProgress }}%</strong></div>
            <v-progress-linear :value="uploadProgress" color="#58d388" background-color="rgba(255,255,255,0.08)" height="8" rounded />
          </div>

          <v-alert v-if="dialogError" type="error" outlined text class="editor-error">{{ dialogError }}</v-alert>
        </v-card-text>

        <v-card-actions class="editor-footer">
          <v-btn text :disabled="saving" @click="closeDialog">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="#58d388" class="black--text save-action" depressed :loading="saving" :disabled="saving" @click="saveProgram">
            <v-icon left>mdi-content-save-outline</v-icon>Programm speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewDialog" max-width="56.25rem" dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="previewBlock" class="media-preview-card">
        <header class="media-preview-header">
          <span class="media-preview-icon"><v-icon>{{ blockIcon(previewBlock.type) }}</v-icon></span>
          <div>
            <small>{{ blockTypeLabel(previewBlock.type) }}</small>
            <h2>{{ previewBlock.title || previewFileName(previewBlock) }}</h2>
          </div>
          <div class="media-preview-actions">
            <v-btn
              v-if="previewBlock.type === 'image' && previewBlock.media"
              icon
              aria-label="Bild herunterladen"
              :loading="downloadingPreview"
              :disabled="downloadingPreview"
              @click="downloadPreviewImage">
              <v-icon>mdi-download-outline</v-icon>
            </v-btn>
            <v-btn icon aria-label="Vorschau schliessen" @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </header>

        <v-card-text class="media-preview-body">
          <div class="media-preview-viewer" :class="`is-${previewBlock.type}`">
            <template v-if="previewBlock.previewUrl">
              <img
                v-if="previewBlock.type === 'image'"
                :src="previewBlock.previewUrl"
                :alt="previewBlock.title || previewFileName(previewBlock)" />
              <video v-else :src="previewBlock.previewUrl" controls playsinline webkit-playsinline preload="metadata" />
            </template>
            <SecuredMedia
              v-else
              :src="mediaPath(previewBlock)"
              :media-type="previewBlock.type"
              :stream-playback="previewBlock.type === 'video'"
              :download-name="previewFileName(previewBlock)"
              :show-app-download-action="false"
              :cache-key="`${editingId}-${previewBlock._id}`"
              eager />
          </div>
          <div v-if="previewBlock.content" class="media-preview-description">
            <span>Beschreibung</span>
            <p>{{ previewBlock.content }}</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4500" top right>{{ snackbar.text }}</v-snackbar>
  </main>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { MAX_VIDEO_SIZE, uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"
import SecuredMedia from "@/views/akademie/SecuredMedia.vue"

const MANAGE_RIGHT = "gf_manageEmployeeCourseContent"
const MAX_BLOCKS = 50
const MAX_IMAGE_SIZE = 25 * 1024 * 1024

export default {
  name: "EmployeeCourseContentAdmin",
  components: { MarketingToolHeader, SecuredMedia },
  data() {
    return {
      programs: [],
      studios: [],
      studioFilter: null,
      studiosLoading: false,
      studioLoadError: "",
      loading: false,
      dialog: false,
      editingId: null,
      valid: false,
      saving: false,
      form: this.emptyForm(),
      blocks: [],
      uploadProgress: 0,
      uploadLabel: "",
      dialogError: "",
      previewDialog: false,
      previewBlock: null,
      downloadingPreview: false,
      snackbar: { show: false, color: "success", text: "" },
      statusItems: [
        { text: "Entwurf", value: "draft" },
        { text: "Veröffentlicht", value: "published" },
        { text: "Archiviert", value: "archived" },
      ],
    }
  },
  computed: {
    canManage() {
      return userService.hasRight(MANAGE_RIGHT)
    },
    studioOptions() {
      const studios = new Map(this.studios.map((studio) => [studio._id, studio]))
      this.programs.forEach((program) => (program.studios || []).forEach((studio) => {
        if (!studios.has(studio._id)) studios.set(studio._id, studio)
      }))
      return [...studios.values()].sort((left, right) => left.name.localeCompare(right.name, "de-CH"))
    },
    filteredPrograms() {
      return this.programs.filter((program) => !this.studioFilter || !program.studios?.length || program.studios.some((studio) => studio._id === this.studioFilter))
    },
  },
  watch: {
    previewDialog(isOpen) {
      if (!isOpen) {
        this.previewBlock = null
        this.downloadingPreview = false
      }
    },
  },
  created() {
    if (this.canManage) this.refreshPrograms()
  },
  beforeDestroy() {
    this.clearPreviews()
  },
  methods: {
    emptyForm() {
      return { title: "HYROX Group", validFrom: this.todayKey(), intro: "", status: "draft", studios: [] }
    },
    todayKey() {
      return this.dateKey(new Date(), "")
    },
    dateKey(value, fallback = null) {
      if (!value) return fallback === null ? this.todayKey() : fallback
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return fallback === null ? this.todayKey() : fallback
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Zurich",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date)
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
      return `${values.year}-${values.month}-${values.day}`
    },
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    async refreshPrograms() {
      await Promise.all([this.loadPrograms(), this.loadStudios()])
    },
    async loadStudios() {
      this.studiosLoading = true
      this.studioLoadError = ""
      try {
        this.studios = (await api.fetchStudios()).filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
      } catch (error) {
        this.studioLoadError = "Die Studioauswahl konnte nicht geladen werden. Bestehende Zuordnungen bleiben erhalten."
      } finally {
        this.studiosLoading = false
      }
    },
    async loadPrograms() {
      this.loading = true
      try {
        this.programs = (await api.employeeCourses.programs.admin.fetchPrograms()) || []
      } catch (error) {
        this.notify("error", "Die HYROX Programme konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    createClientId() {
      return `block-${Date.now()}-${Math.random().toString(16).slice(2)}`
    },
    mapBlock(block) {
      return {
        clientId: this.createClientId(),
        _id: block._id || null,
        type: block.type,
        title: block.title || "",
        content: block.content || "",
        media: block.media || null,
        file: null,
        previewUrl: null,
      }
    },
    openDialog(program = null) {
      this.clearPreviews()
      this.editingId = program && program._id
      this.form = program
        ? {
            title: program.title || "HYROX Group",
            validFrom: this.dateKey(program.validFrom),
            intro: program.intro || "",
            status: program.status || "draft",
            studios: (program.studios || []).map((studio) => studio._id),
          }
        : this.emptyForm()
      this.blocks = program ? (program.blocks || []).map((block) => this.mapBlock(block)) : []
      this.dialogError = ""
      this.uploadLabel = ""
      this.uploadProgress = 0
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    closeDialog() {
      if (this.saving) return
      this.previewDialog = false
      this.previewBlock = null
      this.clearPreviews()
      this.dialog = false
      this.editingId = null
      this.blocks = []
      this.form = this.emptyForm()
      this.dialogError = ""
    },
    addBlock(type) {
      if (this.blocks.length >= MAX_BLOCKS) {
        this.dialogError = `Ein Programm darf maximal ${MAX_BLOCKS} Inhalte enthalten.`
        return
      }
      this.blocks.push(this.mapBlock({ type }))
    },
    moveBlock(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.blocks.length) return
      const blocks = this.blocks.slice()
      const current = blocks[index]
      blocks.splice(index, 1)
      blocks.splice(target, 0, current)
      this.blocks = blocks
    },
    removeBlock(index) {
      const block = this.blocks[index]
      if (this.previewBlock === block) this.previewDialog = false
      this.revokePreview(block)
      this.blocks.splice(index, 1)
    },
    replaceMedia(block) {
      if (this.previewBlock === block) this.previewDialog = false
      this.revokePreview(block)
      block._id = null
      block.media = null
      block.file = null
    },
    setPreview(block, file) {
      this.revokePreview(block)
      if (!file) return
      block.previewUrl = URL.createObjectURL(file)
    },
    revokePreview(block) {
      if (block && block.previewUrl) URL.revokeObjectURL(block.previewUrl)
      if (block) block.previewUrl = null
    },
    clearPreviews() {
      const blocks = this.blocks || []
      blocks.forEach((block) => this.revokePreview(block))
    },
    openMediaPreview(block) {
      if (!block || (!block.media && !block.previewUrl)) return
      this.previewBlock = block
      this.previewDialog = true
    },
    previewFileName(block) {
      if (!block) return "Dokument"
      if (block.media && block.media.originalName) return block.media.originalName
      if (block.file && block.file.name) return block.file.name
      return block.type === "video" ? "Video" : "Bild"
    },
    async downloadPreviewImage() {
      if (!this.previewBlock || this.previewBlock.type !== "image" || !this.previewBlock.media || this.downloadingPreview) return
      this.downloadingPreview = true
      try {
        await api.akademie.downloadFile(this.mediaPath(this.previewBlock), this.previewFileName(this.previewBlock))
      } catch (error) {
        this.notify("error", "Das Bild konnte nicht heruntergeladen werden.")
      } finally {
        this.downloadingPreview = false
      }
    },
    mediaFileRule(file, type) {
      if (!file) return "Datei erforderlich"
      const mimeType = String(file.type || "").toLowerCase()
      if (type === "image" && !mimeType.startsWith("image/") && !/\.(avif|gif|heic|heif|jpe?g|png|webp)$/i.test(file.name || "")) {
        return "Bitte eine Bilddatei wählen"
      }
      if (type === "video" && !mimeType.startsWith("video/") && !/\.(3gp|avi|m4v|mov|mp4|webm)$/i.test(file.name || "")) {
        return "Bitte eine Videodatei wählen"
      }
      const maxSize = type === "video" ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE
      return file.size <= maxSize || (type === "video" ? "Video maximal 5 GB" : `Bild maximal ${Math.floor(maxSize / 1024 / 1024)} MB`)
    },
    validateBlocks() {
      if (this.form.status === "published" && !this.blocks.length) return "Ein veröffentlichtes Programm benötigt mindestens einen Inhalt."
      for (const block of this.blocks) {
        if ((block.type === "text" || block.type === "notice") && !String(block.content || "").trim()) {
          return `${this.blockTypeLabel(block.type)} benötigt einen Inhalt.`
        }
        if ((block.type === "image" || block.type === "video") && !block.media && !block.file) {
          return `${this.blockTypeLabel(block.type)} benötigt eine Datei.`
        }
        if (block.title && block.title.length > 160) return "Ein Titel ist länger als 160 Zeichen."
        if (block.content && block.content.length > 15000) return "Ein Inhalt ist länger als 15000 Zeichen."
      }
      return ""
    },
    async saveProgram() {
      if (this.saving) return
      if (!this.$refs.form || !this.$refs.form.validate()) return
      const blockError = this.validateBlocks()
      if (blockError) {
        this.dialogError = blockError
        return
      }

      this.saving = true
      this.dialogError = ""
      this.uploadLabel = ""
      this.uploadProgress = 0
      const sessions = []
      let programId = this.editingId

      try {
        if (!programId) {
          const created = await api.employeeCourses.programs.admin.createProgram({
            title: this.form.title,
            validFrom: this.form.validFrom,
            intro: this.form.intro,
            studios: this.form.studios,
          })
          programId = created._id
          this.editingId = programId
        }

        const payloadBlocks = []
        for (const block of this.blocks) {
          const payload = {
            ...(block._id ? { _id: block._id } : {}),
            type: block.type,
            title: block.title,
            content: block.content,
          }
          if ((block.type === "image" || block.type === "video") && !block.media) {
            this.uploadLabel = `Lade ${block.file.name} hoch`
            this.uploadProgress = 0
            const session = await api.employeeCourses.programs.admin.prepareMediaUpload(programId, block.file)
            sessions.push(session)
            await uploadCloudflareMedia(block.file, session, {
              onProgress: (loaded, total) => {
                this.uploadProgress = Math.round((loaded * 100) / Math.max(1, total || block.file.size))
              },
            })
            payload.media = session
          }
          payloadBlocks.push(payload)
        }

        await api.employeeCourses.programs.admin.updateProgram(programId, {
          title: this.form.title,
          validFrom: this.form.validFrom,
          intro: this.form.intro,
          status: this.form.status,
          studios: this.form.studios,
          blocks: payloadBlocks,
        })

        sessions.length = 0
        this.clearPreviews()
        this.dialog = false
        this.editingId = null
        this.blocks = []
        await this.loadPrograms()
        this.notify("success", "HYROX Programm gespeichert. Es wurden keine Benachrichtigungen versendet.")
      } catch (error) {
        await Promise.all(
          sessions.map((session) => api.employeeCourses.programs.admin.cancelMediaUpload(programId, session).catch(() => null)),
        )
        this.dialogError =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          error.message ||
          "Das HYROX Programm konnte nicht gespeichert werden."
      } finally {
        this.saving = false
        this.uploadLabel = ""
      }
    },
    async deleteProgram(program) {
      if (!confirm(`HYROX Programm «${program.title}» wirklich löschen? Zugehörige Medien werden ebenfalls entfernt.`)) return
      try {
        await api.employeeCourses.programs.admin.deleteProgram(program._id)
        await this.loadPrograms()
        this.notify("success", "HYROX Programm gelöscht.")
      } catch (error) {
        this.notify("error", "Das HYROX Programm konnte nicht gelöscht werden.")
      }
    },
    mediaPath(block) {
      if (!this.editingId || !block || !block._id) return ""
      return block.type === "video"
        ? api.employeeCourses.programs.getPlaybackPath(this.editingId, block._id)
        : api.employeeCourses.programs.getMediaPath(this.editingId, block._id)
    },
    blockIcon(type) {
      return { text: "mdi-text-box-outline", notice: "mdi-alert-circle-outline", image: "mdi-image-outline", video: "mdi-video-outline" }[type]
    },
    blockTypeLabel(type) {
      return { text: "Text", notice: "Hinweis", image: "Bild", video: "Video" }[type] || "Inhalt"
    },
    statusLabel(status) {
      return { draft: "Entwurf", published: "Veröffentlicht", archived: "Archiviert" }[status] || status
    },
    formatDate(value) {
      return value
        ? `Gültig ab ${new Date(value).toLocaleDateString("de-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric" })}`
        : "Kein Startdatum"
    },
    formatUpdatedAt(value) {
      return value
        ? `Bearbeitet ${new Date(value).toLocaleDateString("de-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric" })}`
        : "Noch nicht bearbeitet"
    },
    formatSize(value) {
      const size = Number(value || 0)
      if (!size) return "Grösse unbekannt"
      if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
      if (size >= 1024 * 1024 * 1024) return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
  },
}
</script>

<style scoped>
.program-admin-page {
  min-height: 100vh;
  padding: 1.5rem;
  color: #f4f4f5;
  background: #101211;
  font-size: 1rem;
}

.program-admin-shell {
  width: min(100%, 90rem);
  margin: 0 auto;
}

.program-admin-content {
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem;
  background: #191b1a;
}

.overview-row,
.editor-header,
.block-header,
.program-card header,
.program-card footer,
.program-meta,
.existing-media-copy,
.upload-state > div {
  display: flex;
  align-items: center;
}

.overview-row {
  justify-content: space-between;
  gap: 1rem;
}

.section-eyebrow,
.program-heading small,
.block-header small {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.overview-row h2,
.editor-header h2 {
  margin: 0.1875rem 0 0.25rem;
  font-size: 1.5rem;
  line-height: 1.25;
}

.overview-row p,
.editor-header p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.create-action,
.secondary-action,
.save-action {
  min-height: 2.75rem;
  border-radius: 0.75rem !important;
  font-size: 0.875rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.publish-note {
  margin: 1rem 0 !important;
  border-radius: 0.875rem;
  font-size: 0.875rem;
}

.program-studio-filter {
  max-width: 28rem;
  margin: 0 0 1.25rem;
}

.program-studio-select {
  margin-bottom: 1.25rem;
}

.program-studio-select ::v-deep .v-select__selections {
  min-width: 0;
  gap: 0.25rem;
}

.program-studio-select ::v-deep .v-chip {
  max-width: 100%;
  height: auto;
  min-height: 2rem;
  font-size: 0.875rem;
}

.program-studio-select ::v-deep .v-chip__content {
  white-space: normal;
  overflow-wrap: anywhere;
  padding: 0.25rem 0;
}

.program-studio-select ::v-deep .v-messages,
.studio-load-error {
  font-size: 0.875rem;
  line-height: 1.5;
}

.program-studios {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 1rem;
}

.program-studios span {
  max-width: 100%;
  padding: 0.25rem 0.625rem;
  border-radius: 0.5rem;
  background: rgba(88, 211, 136, 0.1);
  color: #b0e6c4;
  font-size: 0.75rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.program-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.875rem;
}

.program-card {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid #58d388;
  border-radius: 1rem;
  background: #202220;
}

.program-card.status-draft {
  border-top-color: #f2b705;
}

.program-card.status-archived {
  border-top-color: #818181;
  opacity: 0.78;
}

.program-card header {
  align-items: flex-start;
  gap: 0.75rem;
}

.program-icon,
.editor-icon,
.block-type-icon,
.empty-state > span {
  display: inline-flex;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.16);
}

.program-heading {
  min-width: 0;
  flex: 1;
}

.program-heading h3 {
  margin: 0.1875rem 0 0;
  overflow-wrap: anywhere;
  font-size: 1.125rem;
  line-height: 1.35;
}

.status-badge {
  flex: 0 0 auto;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #d1d1d4;
  background: rgba(255, 255, 255, 0.08);
  font-size: 0.75rem;
  font-weight: 800;
}

.status-badge.published {
  color: #8cf0b3;
  background: rgba(0, 150, 64, 0.18);
}

.status-badge.draft {
  color: #ffdc6a;
  background: rgba(242, 183, 5, 0.14);
}

.program-card > p {
  display: -webkit-box;
  margin: 1rem 0;
  overflow: hidden;
  color: #c7c7ca;
  font-size: 0.875rem;
  line-height: 1.55;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.program-meta {
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: auto;
  color: #98989d;
  font-size: 0.75rem;
}

.program-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
}

.program-meta .v-icon {
  color: inherit;
  font-size: 1rem;
}

.program-card footer {
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.625rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.program-card footer .v-btn {
  font-size: 0.875rem;
  text-transform: none;
}

.loading-state,
.empty-state,
.access-state,
.blocks-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 16rem;
  text-align: center;
}

.loading-state span,
.empty-state p,
.access-state p,
.blocks-empty span {
  color: #a3a3a8;
  font-size: 0.875rem;
}

.empty-state h3,
.access-state h2 {
  margin: 0;
  font-size: 1.25rem;
}

.access-state {
  min-height: 60vh;
}

.access-state > .v-icon {
  color: #58d388;
  font-size: 3rem;
}

.editor-card {
  overflow: hidden;
  border: 1px solid rgba(88, 211, 136, 0.22);
  border-radius: 1.25rem !important;
  background: #171918 !important;
  color: #f4f4f5;
  font-size: 1rem;
}

.editor-header {
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.14), rgba(23, 25, 24, 0.98) 48%);
}

.editor-header > div:nth-child(2) {
  min-width: 0;
  flex: 1;
}

.editor-body {
  padding: 1.25rem !important;
}

.editor-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(12rem, 0.42fr);
  gap: 0.75rem;
}

.status-select {
  max-width: 20rem;
  margin-bottom: 1.5rem;
}

.content-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.5rem 0 0.75rem;
}

.content-heading h3 {
  margin: 0.125rem 0 0;
  font-size: 1.25rem;
}

.content-heading > span {
  color: #a3a3a8;
  font-size: 0.75rem;
  font-weight: 800;
}

.block-list {
  display: grid;
  gap: 0.75rem;
}

.content-block {
  min-width: 0;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #202220;
}

.block-header {
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.block-header > div:nth-child(2) {
  min-width: 0;
  flex: 1;
}

.block-header strong {
  display: block;
  font-size: 1rem;
}

.block-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
}

.media-file-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: #111312;
}

.media-file-icon,
.media-preview-icon {
  display: inline-flex;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.16);
}

.media-file-icon.is-image {
  color: #8ed8ff;
  background: rgba(35, 151, 214, 0.14);
}

.media-file-copy {
  min-width: 0;
}

.media-file-copy small,
.media-file-copy strong,
.media-file-copy span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-file-copy small,
.media-preview-header small,
.media-preview-description > span {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.media-file-copy strong {
  margin: 0.125rem 0;
  color: #ffffff;
  font-size: 0.875rem;
  line-height: 1.4;
}

.media-file-copy span {
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.4;
}

.media-file-actions,
.media-preview-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.media-file-actions .v-btn {
  min-height: 2.625rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  letter-spacing: 0;
  text-transform: none;
}

.media-preview-card {
  display: flex;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(88, 211, 136, 0.22) !important;
  border-radius: 1.25rem !important;
  color: #f4f4f5;
  background: #171918 !important;
  font-size: 1rem;
}

.media-preview-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.16), rgba(23, 25, 24, 0.98) 52%);
}

.media-preview-header > div:not(.media-preview-actions) {
  min-width: 0;
  flex: 1;
}

.media-preview-header h2 {
  margin: 0.1875rem 0 0;
  overflow-wrap: anywhere;
  color: #ffffff;
  font-size: 1.5rem;
  line-height: 1.3;
}

.media-preview-actions {
  flex: 0 0 auto;
}

.media-preview-body {
  overflow-y: auto;
  padding: 1rem !important;
}

.media-preview-viewer {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #080908;
}

.media-preview-viewer.is-video {
  overflow: hidden;
}

.media-preview-viewer.is-image {
  overflow: visible;
  padding: 0.75rem;
}

.media-preview-viewer > img,
.media-preview-viewer > video {
  display: block;
  width: 100%;
  height: auto;
  max-height: none;
  object-fit: contain;
}

.media-preview-viewer.is-video > video {
  aspect-ratio: 16 / 9;
  background: #000000;
}

.media-preview-viewer.is-video ::v-deep .secured-media-wrapper,
.media-preview-viewer.is-video ::v-deep .secured-media-content.video {
  border: 0;
  border-radius: 0;
}

.media-preview-viewer.is-image ::v-deep .secured-media-wrapper {
  min-height: 16rem;
}

.media-preview-viewer.is-image ::v-deep .secured-media-content.image {
  width: 100%;
  height: auto;
  max-height: none;
  object-fit: contain;
}

.media-preview-description {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #202220;
}

.media-preview-description p {
  margin: 0.375rem 0 0;
  color: #d0d0d3;
  font-size: 1rem;
  line-height: 1.65;
  white-space: pre-line;
}

.blocks-empty {
  min-height: 8rem;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  border-radius: 1rem;
}

.blocks-empty .v-icon {
  color: #58d388;
  font-size: 2rem;
}

.add-block-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.625rem;
  margin-top: 0.75rem;
}

.add-block-grid button {
  display: flex;
  min-height: 4rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid rgba(88, 211, 136, 0.24);
  border-radius: 0.75rem;
  color: #f4f4f5;
  background: rgba(0, 150, 64, 0.08);
  font-size: 0.875rem;
  font-weight: 800;
}

.add-block-grid button:hover,
.add-block-grid button:focus-visible {
  border-color: #58d388;
  background: rgba(0, 150, 64, 0.16);
  outline: none;
}

.add-block-grid .v-icon {
  color: #58d388;
}

.upload-state {
  margin-top: 1rem;
  padding: 0.875rem;
  border-radius: 0.75rem;
  background: rgba(0, 150, 64, 0.1);
}

.upload-state > div {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.upload-state span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.editor-error {
  margin: 1rem 0 0 !important;
  font-size: 0.875rem;
}

.editor-footer {
  padding: 1rem 1.25rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.editor-footer .v-btn {
  font-size: 0.875rem;
  text-transform: none;
}

@media (max-width: 72rem) {
  .program-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 48rem) {
  .program-admin-page {
    padding: 0.75rem;
  }

  .program-admin-content {
    padding: 1rem;
  }

  .overview-row {
    align-items: stretch;
    flex-direction: column;
  }

  .create-action {
    width: 100%;
  }

  .program-grid,
  .editor-fields {
    grid-template-columns: minmax(0, 1fr);
  }

  .status-select {
    max-width: none;
  }
}

@media (max-width: 37.5rem) {
  .program-admin-page {
    padding: 0;
  }

  .program-admin-content {
    margin-top: 0.5rem;
    padding: 0.75rem;
    border-right: 0;
    border-left: 0;
    border-radius: 0;
  }

  .editor-card {
    border: 0;
    border-radius: 0 !important;
  }

  .editor-header,
  .editor-body,
  .editor-footer {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }

  .editor-icon {
    display: none;
  }

  .editor-header h2 {
    font-size: 1.25rem;
  }

  .content-block {
    padding: 0.75rem;
  }

  .block-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .block-actions {
    width: 100%;
    justify-content: flex-end;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding-top: 0.375rem;
  }

  .add-block-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .media-file-row {
    grid-template-columns: 2.5rem minmax(0, 1fr);
    align-items: flex-start;
  }

  .media-file-icon {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .media-file-actions {
    width: 100%;
    grid-column: 1 / -1;
    align-items: stretch;
    flex-direction: column;
  }

  .media-file-actions .v-btn {
    width: 100%;
  }

  .media-preview-card {
    min-height: 100vh;
    min-height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .media-preview-header {
    gap: 0.625rem;
    padding: 1rem;
  }

  .media-preview-icon {
    display: none;
  }

  .media-preview-header h2 {
    font-size: 1.25rem;
  }

  .media-preview-body {
    padding: 0.75rem !important;
  }

  .editor-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: #171918;
  }
}
</style>
